/**
 * Session: who is signed in, and are they actually an admin.
 *
 * ── Admin is a claim, never a document field ───────────────────────────────
 *
 * The gate below reads `role` off the **ID token**, not off `users/{uid}`.
 * That is not a stylistic choice: `firestore.rules` authorises on
 * `request.auth.token.role`, and every callable checks
 * `requireRole(req, 'admin')` server-side. A console that gated on a
 * document field would let anyone who could write that field into the UI,
 * and would then fail on every action — showing a full console that does
 * nothing. Reading the claim means the UI's idea of "admin" is the same fact
 * the server enforces.
 *
 * Claims are granted only by `scripts/provision-admin.mjs`, by UID. Signing
 * in here with the same phone number gives the same Firebase UID as the
 * Flutter admin app, so an already-provisioned admin needs nothing new.
 *
 * ── Why the claims listener exists ─────────────────────────────────────────
 *
 * An ID token is cached for an hour, so a freshly granted admin claim would
 * not appear until it expired or the tab was reloaded. The apps solve this by
 * having the server bump `users/{uid}.claims_updated_at`; the client watches
 * its own user document and forces a token refresh when it moves
 * (CLAUDE.md §12). This does the same, so provisioning takes effect here
 * within a second rather than within an hour.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  onAuthStateChanged,
  onIdTokenChanged,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth'
import { doc, onSnapshot } from 'firebase/firestore'
import { auth, db } from './firebase'

export interface Claims {
  role: string | null
  shopId: string | null
  verified: boolean
}

export type SessionStage =
  /** Still resolving auth and claims. Render nothing decisive yet. */
  | 'loading'
  /** Nobody signed in. */
  | 'signedOut'
  /** Signed in, but not an admin — the one case worth its own screen. */
  | 'notAdmin'
  /** Signed in with the admin claim. */
  | 'admin'

interface Session {
  stage: SessionStage
  user: User | null
  claims: Claims

  /**
   * May change other people's roles.
   *
   * Gates the role editor on Settings. Cosmetic only — `setUserRole` requires
   * `super_admin` server-side, so hiding the card stops an ordinary admin
   * being offered something that would come back permission-denied; it is not
   * what enforces the rule.
   */
  isSuperAdmin: boolean
  signOut: () => Promise<void>
}

const EMPTY: Claims = { role: null, shopId: null, verified: false }

const SessionContext = createContext<Session>({
  stage: 'loading',
  user: null,
  claims: EMPTY,
  isSuperAdmin: false,
  signOut: async () => {},
})

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [claims, setClaims] = useState<Claims>(EMPTY)
  const [resolved, setResolved] = useState(false)

  // Auth state, and the claims that come with each token.
  useEffect(() => {
    const stopAuth = onAuthStateChanged(auth, (next) => {
      setUser(next)
      if (!next) {
        setClaims(EMPTY)
        setResolved(true)
      }
    })

    // `onIdTokenChanged` rather than reading once: it also fires on refresh,
    // which is what makes the forced refresh below actually land.
    const stopToken = onIdTokenChanged(auth, async (next) => {
      if (!next) return
      const token = await next.getIdTokenResult()
      setClaims({
        role: (token.claims.role as string) ?? null,
        shopId: (token.claims.shop_id as string) ?? null,
        verified: token.claims.verified === true,
      })
      setResolved(true)
    })

    return () => {
      stopAuth()
      stopToken()
    }
  }, [])

  // The `claims_updated_at` listener — see the header.
  useEffect(() => {
    if (!user) return
    let first = true
    return onSnapshot(doc(db, 'users', user.uid), (snap) => {
      // Skip the first delivery: it is the current state, not a change, and
      // forcing a refresh on it would cost a round trip on every sign-in.
      if (first) {
        first = false
        return
      }
      if (snap.data()?.claims_updated_at) void user.getIdToken(true)
    })
  }, [user])

  const value = useMemo<Session>(() => {
    const stage: SessionStage = !resolved
      ? 'loading'
      : !user
        ? 'signedOut'
        : // `super_admin` is an admin who may also change roles (26 Oct
          // 2026). It passes this gate exactly as `admin` does — the extra
          // privilege is expressed by `isSuperAdmin` below, which gates the
          // role editor, not by a separate stage.
          claims.role === 'admin' || claims.role === 'super_admin'
          ? 'admin'
          : 'notAdmin'

    return {
      stage,
      user,
      claims,
      isSuperAdmin: claims.role === 'super_admin',
      signOut: async () => {
        await fbSignOut(auth)
      },
    }
  }, [resolved, user, claims])

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  )
}

export function useSession(): Session {
  return useContext(SessionContext)
}
